import React, { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Loading from 'components/Loading';
import {
  useAcceptTermsAndConditionsMutation,
  useTermsAndConditionsQuery
} from 'providers/api';
import { useAuthorisation } from 'providers/authorisation';
import { useNavigate } from 'react-router-dom';
import TermsAndConditionsDialog from './TermsAndConditionsDialog';

interface TermsAndConditionsGateProps {
  children: ReactNode;
}

const TermsAndConditionsGate: React.FC<TermsAndConditionsGateProps> = ({ children }) => {
  const navigate = useNavigate();
  const {
    state: { user, loading: authoriseLoading },
  } = useAuthorisation();

  const { data, isLoading, isError } = useTermsAndConditionsQuery(undefined, {
    enabled: !!user?.id && !authoriseLoading,
  });

  const acceptMutation = useAcceptTermsAndConditionsMutation();

  if (authoriseLoading || isLoading) {
    return <Loading />;
  }

  // If the call fails (e.g. user not yet provisioned) let the app render so it can
  // surface its own error rather than locking the user out.
  if (isError) {
    return <>{children}</>;
  }

  const hasAccepted = !!data?.isAccepted;

  if (hasAccepted) {
    return <>{children}</>;
  }

  const handleAccept = () => {
    acceptMutation.mutate(undefined, {
      onSuccess: () => {
        // Send through the wildcard so ProjectsRootRedirect picks the right landing
        // page (single project → stopes view, otherwise → /projects).
        navigate('/', { replace: true });
      },
    });
  };

  return (
    <>
      <Box
        aria-hidden
        sx={{
          filter: 'blur(6px)',
          pointerEvents: 'none',
          userSelect: 'none',
          height: '100%',
        }}
      >
        {children}
      </Box>
      <TermsAndConditionsDialog
        open
        isSubmitting={acceptMutation.isLoading}
        onAccept={handleAccept}
      />
    </>
  );
};

export default TermsAndConditionsGate;
